import {useCallback, useEffect, useState} from "react";
import {Clock, Tag} from "lucide-react";
import {
  createAdminProductOffer,
  deleteAdminProductOffer,
  listAdminProductOffers,
  listAdminProducts,
  updateAdminProductOffer,
  type AdminProductOffer
} from "@corporatesclub/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs, AdminToggleField, AdminRowTrailing} from "./ui";

const emptyForm = {status: 0, productId: 0, discount: 10, expiresAt: ""};

export function ProductOffersView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("produse");
  const [items, setItems] = useState<AdminProductOffer[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [productOpts, setProductOpts] = useState<{id: number; label: string}[]>([]);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminProductOffers({page, pageSize: 20, search: search || undefined})
      .then((r) => { setItems(r.items); setTotalPages(r.totalPages); })
      .catch((e: Error) => setErr(e.message));
  }, [page, search]);
  useEffect(() => { load(); }, [page]);
  useEffect(() => {
    listAdminProducts({page: 1, pageSize: 100}).then((r) => {
      setProductOpts(r.items.map((p) => ({id: p.id, label: `${p.cod} ${p.nume}`})));
    }).catch(() => {});
  }, []);
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(o: AdminProductOffer) {
    setOpenId(o.id);
    setForm({status: o.status, productId: o.productId, discount: o.discount, expiresAt: o.expiresAt});
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          await createAdminProductOffer(form);
          adminToast.success("Offer added.");
        } else if(typeof openId === "number") {
          await updateAdminProductOffer(openId, form);
          adminToast.success("Offer saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete offer?", async () => {
      await deleteAdminProductOffer(id);
      closeModal(false);
      load();
      adminToast.success("Offer deleted.");
    });
  }
  function offerFormFields() {
    return (
      <div className="admin-detail-panel__form">
        <AdminToggleField
          title="Active offer"
          description="Inactive offers are hidden from the storefront."
          checked={form.status === 0}
          onCheckedChange={(v) => setForm({...form, status: v ? 0 : 1})}
        />
        <AdminField label="Product">
          <Select value={String(form.productId)} onValueChange={(v) => setForm({...form, productId: Number(v)})}>
            <SelectTrigger><SelectValue placeholder="Product" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Produs</SelectItem>
              {productOpts.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </AdminField>
        <AdminField label="Discount %">
          <Input type="number" value={form.discount} onChange={(e) => setForm({...form, discount: Number(e.target.value)})} />
        </AdminField>
        <AdminField label="Expiry date">
          <Input value={form.expiresAt} onChange={(e) => setForm({...form, expiresAt: e.target.value})} placeholder="Expiry date" />
        </AdminField>
      </div>
    );
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.produse.title}
      description="Promotional product offers"
      breadcrumbs={[
        {label: ADMIN_SECTION_NAV.produse.title, href: "/vadmin/produse"},
        {label: "Offers"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add offer" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="produse" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(o) => o.id}
        getTitle={(o) => o.productName}
        getSubtitle={(o) => o.expiresAt}
        getMeta={(o) => (
          <>
            <AdminBadge variant={o.expired ? "danger" : "secondary"}><Clock className="h-3 w-3" /> {o.expiresAt}</AdminBadge>
            <AdminBadge variant="secondary">{o.discount}%</AdminBadge>
          </>
        )}
        getSearchText={(o) => adminSearchText(o.id, o.productName, o.productId, o.discount, o.expiresAt, o.status, o.expired)}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No offers"
        emptyDescription="Add a new offer or adjust filters."
        searchPlaceholder="Search product, discount, data..."
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={() => { setPage(1); load(); }}
        clientFilter={false}
        getRowActions={(o) => (
          <AdminRowTrailing onDelete={() => removeItem(o.id)} deleteLabel="Delete offer" />
        )}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New offer" : "Edit offer"}
        icon={Tag}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        {offerFormFields()}
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
